/**
 * Последната позната каса — `localStorage["edge_last_known"] = { ip, port,
 * enabled }` (§15.4 на договора, docs/edge.md §Старт без токен).
 *
 * Всички останали ключове на резерва са вързани за носител: билетът
 * (`edge_ticket`) е издаден под токен, кешираният режим (`edge_mode`) описва
 * сесия, кешираната самоличност — потребител. Затова изходът ги трие.
 * Адресът на касата обаче НЕ е нито билет, нито самоличност: той е свойство
 * на ОБЕКТА и е единственото, което терминал, от когото някой е излязъл,
 * трябва да знае, за да се върне към живот без интернет. Оттам и отделният
 * ключ — `clearToken()` (`lib/auth.ts`) съзнателно НЕ го чисти.
 *
 * Пише се от `applyConfig()` (`edgeRuntime.ts`) при всяка приложена
 * конфигурация — тоест и когато облакът каже `enabled: false` (изключен
 * резерв: следващият старт без токен не тръгва да търси каса). Чете се само
 * при старт БЕЗ токен (`resumeLoggedOutLocalMode` в `edgeMode.ts`); с токен
 * източникът на истина за адреса си остава `edge_config`.
 */
import {
  EDGE_API_VERSION,
  EDGE_LAST_KNOWN_STORAGE_KEY,
  edgeStorageGet,
  edgeStorageSet,
  type EdgeConfig,
} from "./edgeState";

export interface EdgeLastKnown {
  /** LAN IP на главната каса; null = резервът е бил включен без обявена каса. */
  ip: string | null;
  port: number;
  /** Бил ли е резервът включен за ресторанта при последния отговор на облака. */
  enabled: boolean;
}

export function readEdgeLastKnown(): EdgeLastKnown | null {
  const raw = edgeStorageGet(EDGE_LAST_KNOWN_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<EdgeLastKnown>;
    if (!parsed || typeof parsed !== "object") return null;
    return {
      ip: typeof parsed.ip === "string" ? parsed.ip : null,
      port: typeof parsed.port === "number" ? parsed.port : 8787,
      enabled: parsed.enabled === true,
    };
  } catch {
    return null;
  }
}

/** Запомня адреса от приложената конфигурация. Записът е идемпотентен —
 * конфигурацията се опреснява на 5 min, а еднаквите стойности не се пишат. */
export function rememberEdgeLastKnown(config: EdgeConfig) {
  const value = JSON.stringify({
    ip: config.ip,
    port: config.port,
    enabled: config.enabled,
  } satisfies EdgeLastKnown);
  if (value === edgeStorageGet(EDGE_LAST_KNOWN_STORAGE_KEY)) return;
  edgeStorageSet(EDGE_LAST_KNOWN_STORAGE_KEY, value);
}

/**
 * Конфигурация от запомнения адрес — за старт без токен, когато кешът
 * `edge_config` липсва (изчистен storage, ново устройство след възстановяване
 * от бекъп). `masterClientId`/`cloudMode` не се знаят и не се ползват за
 * нищо в този път: те влизат наново с първия `GET /configuration/edge`.
 */
export const edgeConfigFromLastKnown = (
  lastKnown: EdgeLastKnown,
): EdgeConfig => ({
  enabled: lastKnown.enabled,
  ip: lastKnown.ip,
  port: lastKnown.port,
  masterClientId: null,
  cloudMode: "cloud",
  edgeApi: EDGE_API_VERSION,
});
